import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const EMPTY = { name: "", description: "", icon: "", display_order: 0, status: "active" };

export default function SuperCompanyTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/super/company-types");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/super/company-types/${editing}`, form);
      else await api.post("/super/company-types", form);
      toast.success("Saved");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const deactivate = async (id) => {
    if (!confirm("Deactivate this type?")) return;
    await api.delete(`/super/company-types/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="super-company-types-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Catalog</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Company types</h1>
          <p className="text-sm text-zinc-500 mt-1">Business categories companies pick during onboarding.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-white text-black hover:bg-zinc-200 rounded-full" data-testid="super-ct-add-btn"><Plus size={16} className="mr-1" /> Add type</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181B] border-white/10 text-zinc-200">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New company type"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="super-ct-form">
              <div><Label>Name *</Label><Input className="bg-[#0a0a0c] border-white/10" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="super-ct-name-input" /></div>
              <div><Label>Description</Label><Textarea className="bg-[#0a0a0c] border-white/10" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Display order</Label><Input className="bg-[#0a0a0c] border-white/10" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) || 0 })} /></div>
                <div className="flex items-end gap-3 pb-1">
                  <Switch checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} />
                  <span className="text-sm">Active</span>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="super-ct-save-btn">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#18181B] border border-white/5 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5"><TableHead className="text-zinc-400">Name</TableHead><TableHead className="text-zinc-400">Companies</TableHead><TableHead className="text-zinc-400">Order</TableHead><TableHead className="text-zinc-400">Status</TableHead><TableHead className="text-zinc-400 text-right">Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} className="border-white/5 hover:bg-white/5" data-testid={`super-ct-row-${t.id}`}>
                <TableCell className="text-zinc-200">{t.name}</TableCell>
                <TableCell className="text-zinc-300">{t.companies_count}</TableCell>
                <TableCell className="text-zinc-400">{t.display_order}</TableCell>
                <TableCell><Badge variant="outline" className={t.status === "active" ? "bg-emerald-900/40 text-emerald-300 border-emerald-800" : "bg-zinc-800 text-zinc-400 border-zinc-700"}>{t.status}</Badge></TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:bg-white/10" onClick={() => { setForm(t); setEditing(t.id); setOpen(true); }} data-testid={`super-ct-edit-${t.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/20" onClick={() => deactivate(t.id)} data-testid={`super-ct-deactivate-${t.id}`}>Deactivate</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
